// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Layers, StopCircle, Plus, Sparkles, CalendarPlus, MapPin } from 'lucide-react'
import { formatUsDate } from '../../api/format'
import SearchableSelect from '../../components/common/SearchableSelect'
import CarePlanPanel from '../carerx/CarePlanPanel'
import { pcwPhiValues } from '../pot/pcw/pcwAiClient'
import * as api from '../../api/client'
import TherapyDocumentButtons from '../carerx/TherapyDocumentButtons'
import EnrollmentDoor from '../carerx/EnrollmentDoor'
import { identityPreset } from '../carerx/patientIdentity'
import { visibleAnchorWarning } from '../carerx/anchorWarning'

/**
 * CareRx v2 §10/§3.1 — Therapy lanes on the patient detail page.
 * One lane per concurrent therapy (drug), with lifecycle state, cycle, dates, and an
 * end-of-care action for active lanes. Above the lanes, the §3.1 twin/identity banner
 * warns when another patient in the pharmacy shares this patient's last name + DOB —
 * confirm the right chart before acting. Renders nothing if the caller lacks CareRx access.
 */

const STATE_COLOR: Record<string, { bg: string; fg: string }> = {
  Enrolling:          { bg: '#e0ecff', fg: '#1d4ed8' },
  PendingMDSignature: { bg: '#fdf2c8', fg: '#92400e' },
  Active:             { bg: '#dcfce7', fg: '#166534' },
  OnHold:             { bg: '#fee2e2', fg: '#991b1b' },
  Completed:          { bg: '#e5e7eb', fg: '#374151' },
  Discontinued:       { bg: '#e5e7eb', fg: '#374151' },
  Transferred:        { bg: '#e5e7eb', fg: '#374151' },
  LostToFollowup:     { bg: '#e5e7eb', fg: '#374151' },
  Deceased:           { bg: '#e5e7eb', fg: '#374151' },
  Cancelled:          { bg: '#e5e7eb', fg: '#374151' },
}

const END_STATES = ['Completed', 'Discontinued', 'Transferred', 'LostToFollowup', 'Deceased', 'Cancelled']

export default function PatientTherapyLanes({ patientId }: { patientId: string }) {
  const [lanes, setLanes] = useState<api.CareRxTherapyLane[] | null>(null)
  // UAT 2026-07-29 #4: edit an existing enrollment (extras + site) via the door's edit mode.
  const [editLane, setEditLane] = useState<api.CareRxTherapyLane | null>(null)
  // G-8: outreach panel per lane (status/outcome/callback/attempts/log).
  const [orOpen, setOrOpen] = useState<string | null>(null)
  const [orState, setOrState] = useState<api.OutreachState | null>(null)
  const [orForm, setOrForm] = useState({ status: '', outcome: '', callback_on: '', method: 'call', notes: '' })
  const loadOutreach = (tid: string) => api.getCareRxOutreach(tid).then(o => {
    setOrState(o); setOrForm(f => ({ ...f, status: o.status ?? '', outcome: o.outcome ?? '', callback_on: o.callback_on ? String(o.callback_on).slice(0, 10) : '' }))
  }).catch(() => setOrState(null))
  const [twins, setTwins] = useState<api.CareRxTwin[]>([])
  const [collision, setCollision] = useState(false)
  const [ending, setEnding] = useState<string | null>(null) // therapy id with the end-care panel open
  const [siteFor, setSiteFor] = useState<string | null>(null) // therapy id with the site-of-care panel open
  const [planFor, setPlanFor] = useState<string | null>(null) // therapy id with the care-plan panel open
  const [notesFor, setNotesFor] = useState<string | null>(null)
  const [docsFor, setDocsFor] = useState<string | null>(null)
  const [docItems, setDocItems] = useState<{ key: string; label: string; status: string; at: string | null }[]>([])
  const [histFor, setHistFor] = useState<string | null>(null)
  const [hist, setHist] = useState<any | null>(null)
  const [notes, setNotes] = useState<{ id: string; lane: string; body: string; created_at: string }[]>([])
  const [noteLane, setNoteLane] = useState('intake_a')
  const [noteBody, setNoteBody] = useState('')
  const [banner, setBanner] = useState<{ name?: string; dob?: string; mrn?: string; allergies?: string } | null>(null)
  const [siteMode, setSiteMode] = useState('')
  const [siteReason, setSiteReason] = useState('')
  const [siteMsg, setSiteMsg] = useState('')
  const [endState, setEndState] = useState(END_STATES[0])
  const [endReason, setEndReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!patientId) return
    api.listCareRxTherapies(patientId)
      .then(d => setLanes(d.therapies))
      .catch(() => setLanes(null)) // no access — stay quiet
    api.getCareRxTwins(patientId)
      .then(d => { setCollision(d.collision); setTwins(d.twins) })
      .catch(() => { setCollision(false); setTwins([]) })
  }, [patientId])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    // ASP-CRX-21 (spec §C): the patient banner on clinical screens — name, DOB, allergies, ID.
    if (!patientId) return
    api.getPatient(patientId).then(r => {
      const f = (k: string) => { const v: any = r.patient?.[k]; return v && typeof v === 'object' && 'value' in v ? v.value : v }
      setBanner({ name: f('display_name'), dob: f('date_of_birth'), mrn: f('mrn'), allergies: f('allergies') })
    }).catch(() => setBanner(null))
  }, [patientId])

  // D-CRX-21: enrollment door on the profile, specialty-aware via the drug_reference link.
  const [doorOpen, setDoorOpen] = useState(false)
  const [potFor, setPotFor] = useState<string | null>(null)
  const [potDate, setPotDate] = useState('')
  const [potAgency, setPotAgency] = useState('')
  const [agencies, setAgencies] = useState<{ id: string; name: string }[]>([])
  const [potBusy, setPotBusy] = useState(false)
  const [potMsg, setPotMsg] = useState('')
  useEffect(() => {
    api.listCareRxAgencies().then(r => setAgencies((r.items ?? []).filter(a => a.status === 'active'))).catch(() => setAgencies([]))
  }, [])
  const [hint, setHint] = useState<api.CareRxEnrollmentHint | null>(null)
  useEffect(() => {
    api.getCareRxEnrollmentHint(patientId).then(setHint).catch(() => setHint(null))
  }, [patientId])

  if (!lanes) return null
  // Wave-1 #1: both doors name the patient with DOB (the banner's read), not "this patient".
  const doorPatient = identityPreset({ id: patientId, name: banner?.name ?? null, dob: banner?.dob ?? null })

  const endCare = async (therapyId: string) => {
    if (!endReason.trim()) { setErr('An end-of-care reason is required.'); return }
    setBusy(true); setErr(null)
    try {
      await api.transitionCareRxTherapy(therapyId, { state: endState, reason: endReason.trim() })
      setEnding(null); setEndReason(''); setEndState(END_STATES[0])
      load()
    } catch (e: any) {
      setErr(e?.message || 'Transition failed')
    } finally { setBusy(false) }
  }

  return (
    <>
      {banner && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 10, margin: '14px 0 0', fontSize: 12.5, background: 'var(--color-surface)' }}>
          {/* ASP-CRX-21: the patient banner — name, DOB, ID, allergies (red when present) */}
          <b style={{ fontSize: 14 }}>{banner.name || 'Patient'}</b>
          {banner.dob ? <span>DOB {formatUsDate(banner.dob)}</span> : null}
          {banner.mrn ? <span>ID {banner.mrn}</span> : null}
          <span style={{ color: banner.allergies ? '#b91c1c' : 'var(--color-text-mute)', fontWeight: banner.allergies ? 700 : 400 }}>Allergies: {banner.allergies || 'none recorded'}</span>
        </div>
      )}
      {collision && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid #f59e0b',
          background: '#fffbeb', color: '#92400e', borderRadius: 12, padding: '10px 14px', margin: '20px 0 0', fontSize: 13 }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Possible identity collision.</strong> Another patient shares this last name and date of birth —
            confirm you have the right chart:&nbsp;
            {twins.map((t, i) => (
              <span key={t.id}>{i > 0 ? '; ' : ''}{t.display_name || t.id} · DOB {formatUsDate(t.date_of_birth)}</span>
            ))}
          </div>
        </div>
      )}

      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '28px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Layers size={18} style={{ color: 'var(--color-primary)' }} /> Therapies ({lanes.length})
        <span style={{ flex: 1 }} />
        {hint && hint.count > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
            background: hint.specialty ? '#f3e8ff' : '#fffbeb', color: hint.specialty ? '#7c3aed' : '#92400e',
            border: `1px solid ${hint.specialty ? '#a855f7' : '#d97706'}` }}>
            <Sparkles size={12} /> {hint.specialty ? 'Specialty rx on profile' : 'Formulary rx on profile'}: {hint.candidates[0]?.name}{hint.count > 1 ? ` +${hint.count - 1}` : ''}
          </span>
        )}
        <button onClick={() => setDoorOpen(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 13px', borderRadius: 8,
          fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--color-primary)', background: 'var(--color-primary)', color: '#fff' }}>
          <Plus size={13} /> New enrollment
        </button>
      </h2>
      {doorOpen && (
        <div style={{ margin: '0 0 12px' }}>
          <EnrollmentDoor
            presetPatient={doorPatient}
            presetFormularyId={hint && hint.count === 1 ? hint.candidates[0].formulary_id : undefined}
            onClose={() => setDoorOpen(false)}
            onCreated={() => { setDoorOpen(false); load() }} />
        </div>
      )}
      {editLane && (
        <div style={{ margin: '0 0 12px' }}>
          <EnrollmentDoor
            presetPatient={doorPatient}
            editTherapy={{ therapy_id: editLane.id, drug_name: editLane.drug_name, formulary_id: editLane.formulary_id, site: editLane.care_delivery_mode ?? null }}
            onClose={() => setEditLane(null)}
            onCreated={() => { setEditLane(null); load() }} />
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {lanes.length === 0 && (
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-surface)',
            padding: 16, color: 'var(--color-text-mute)', fontSize: 14 }}>
            No therapies yet — a therapy is created automatically with the first Plan of Care for a drug.
          </div>
        )}
        {lanes.map(t => {
          const c = STATE_COLOR[t.state] ?? STATE_COLOR.Completed
          return (
            <div key={t.id} style={{ border: '1px solid var(--color-border)', borderRadius: 12,
              background: 'var(--color-surface)', padding: '12px 14px' }}>
              {orOpen === t.id && (
                <div style={{ margin: '8px 0', border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5 }}>
                  {!orState ? 'Loading outreach…' : (<>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, alignItems: 'end' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11.5, color: 'var(--color-text-mute)' }}>Status
                        <SearchableSelect value={orForm.status} onChange={(v: string) => setOrForm({ ...orForm, status: v })} ariaLabel="Outreach status" placeholder="—"
                          options={['pending', 'in_progress', 'contacted', 'unreachable', 'not_needed'].map(v => ({ value: v, label: v.replace('_', ' ') }))} /></label>
                      {/* Outcome hidden until CONTACTED (mockup rule; API enforces too) */}
                      {orForm.status === 'contacted' ? (
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11.5, color: 'var(--color-text-mute)' }}>Outcome
                          <SearchableSelect value={orForm.outcome} onChange={(v: string) => setOrForm({ ...orForm, outcome: v })} ariaLabel="Outreach outcome" placeholder="—"
                            options={['scheduled', 'self_scheduling', 'callback_requested', 'needs_prescriber', 'needs_insurance', 'reschedule', 'declined'].map(v => ({ value: v, label: v.replace(/_/g, ' ') }))} /></label>) : <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', alignSelf: 'center' }}>Outcome unlocks when contacted</div>}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11.5, color: 'var(--color-text-mute)' }}>Callback
                        <input type="date" value={orForm.callback_on} onChange={e => setOrForm({ ...orForm, callback_on: e.target.value })} style={{ padding: '4px 7px', borderRadius: 6, border: '1px solid var(--color-border)' }} /></label>
                      <div style={{ fontSize: 11.5 }}>Attempts: <b>{orState.attempts}</b> / floor {orState.ltfu_floor}
                        {orState.ltfu_candidate && <span style={{ color: '#b91c1c', fontWeight: 700 }}> · LTFU candidate</span>}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button onClick={() => api.setCareRxOutreach(t.id, { status: orForm.status || undefined, outcome: orForm.status === 'contacted' ? (orForm.outcome || undefined) : undefined, callback_on: orForm.callback_on || undefined }).then(() => loadOutreach(t.id)).catch(e => alert(e instanceof Error ? e.message : 'Save failed'))}
                        style={{ fontSize: 11.5, padding: '4px 12px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-primary-bg, #eef5fc)', cursor: 'pointer', fontWeight: 600 }}>Save outreach</button>
                      <div style={{ width: 110 }}><SearchableSelect value={orForm.method} onChange={(v: string) => setOrForm({ ...orForm, method: v })} ariaLabel="Attempt method"
                        options={['call', 'text', 'email', 'portal', 'other'].map(v => ({ value: v, label: v }))} /></div>
                      <input placeholder="attempt notes…" value={orForm.notes} onChange={e => setOrForm({ ...orForm, notes: e.target.value })}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 11.5, flex: 1, minWidth: 140 }} />
                      <button onClick={() => api.logCareRxOutreachAttempt(t.id, { method: orForm.method, notes: orForm.notes || undefined }).then(() => { setOrForm(f => ({ ...f, notes: '' })); loadOutreach(t.id) }).catch(e => alert(e instanceof Error ? e.message : 'Log failed'))}
                        style={{ fontSize: 11.5, padding: '4px 12px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer' }}>+ Log attempt</button>
                    </div>
                    {orState.log.length > 0 && (
                      <div style={{ marginTop: 8, maxHeight: 140, overflowY: 'auto', borderTop: '0.5px solid var(--color-border)', paddingTop: 6 }}>
                        {orState.log.map((l, i) => (
                          <div key={i} style={{ fontSize: 11.5, color: 'var(--color-text-mute)', padding: '2px 0' }}>
                            {new Date(l.attempted_at).toLocaleString('en-US')} · <b>{l.source}</b>{l.method ? ` · ${l.method}` : ''}{l.outcome ? ` · ${l.outcome}` : ''}{l.notes ? ` — ${l.notes}` : ''}
                          </div>))}
                      </div>)}
                  </>)}
                </div>)}
              {/* UAT 2026-07-29 #3: persisted anchor violation banner (G-19 warn-not-block).
                  Wave-1 #26: the PA cause is not shown (the stored record keeps it). */}
              {visibleAnchorWarning(t.anchor_warning) && (
                <div data-testid="lane-anchor-warning" style={{ fontSize: 12, color: '#7f1d1d', background: '#fef2f2', border: '0.5px solid #dc2626',
                  borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>⚠ {visibleAnchorWarning(t.anchor_warning)}</div>)}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{t.drug_name || 'Drug not set'}</span>
                {!t.is_terminal && (
                  <button onClick={() => { setDoorOpen(false); setEditLane(t) }} title="Edit this enrollment (dose, dates, outreach, site of care)"
                    style={{ fontSize: 11, padding: '2px 9px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-primary)' }}>
                    Edit enrollment</button>)}
                <TherapyDocumentButtons therapyId={t.id}
                  buttonStyle={{ fontSize: 11, padding: '2px 9px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-mute)' }} />
                {!t.is_terminal && (
                  <button onClick={() => { if (orOpen === t.id) { setOrOpen(null); return } setOrOpen(t.id); setOrState(null); loadOutreach(t.id) }}
                    style={{ fontSize: 11, padding: '2px 9px', borderRadius: 7, border: '1px solid var(--color-border)', background: orOpen === t.id ? 'var(--color-primary-bg, #eef5fc)' : 'transparent', cursor: 'pointer', color: 'var(--color-text-mute)' }}>
                    Outreach{t.anchor_warning != null || true ? '' : ''}</button>)}
                {t.authorized != null && (
                  <span style={{ fontSize: 11.5, color: (t.remaining ?? 1) === 0 ? '#b91c1c' : 'var(--color-text-mute)' }}>
                    {t.dispensed}/{t.authorized} dispensed · {t.remaining} left
                  </span>
                )}
                <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: c.bg, color: c.fg }}>
                  {t.state.replace(/([a-z])([A-Z])/g, '$1 $2')}
                </span>
                <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>Cycle {t.cycle_no}</span>
                <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                  {t.started_at ? `Started ${formatUsDate(t.started_at)}` : `Created ${formatUsDate(t.created_at)}`}
                  {t.ended_at ? ` · Ended ${formatUsDate(t.ended_at)}` : ''}
                </span>
                {!t.is_terminal && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                        border: '1px solid var(--color-primary)', borderRadius: 8, padding: '4px 10px',
                        background: 'transparent', color: 'var(--color-primary)', cursor: 'pointer' }}
                      onClick={() => { setPotFor(potFor === t.id ? null : t.id); setPotMsg(''); setPotDate(''); setPotAgency('') }}>
                      <CalendarPlus size={14} /> Start POT
                    </button>
                    <button
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                        border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 10px',
                        background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
                      onClick={() => { setSiteFor(siteFor === t.id ? null : t.id); setSiteMsg(''); setSiteReason(''); setSiteMode((t as any).care_delivery_mode || '') }}>
                      <MapPin size={14} /> Site of care
                    </button>
                    <button
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                        border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 10px',
                        background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
                      onClick={() => setPlanFor(planFor === t.id ? null : t.id)}>
                      <CalendarPlus size={14} /> Care plan
                    </button>
                    <button
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                        border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 10px',
                        background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
                      onClick={async () => {
                        const open = notesFor === t.id ? null : t.id
                        setNotesFor(open); setNotes([])
                        if (open) { try { setNotes((await api.listCareRxTherapyNotes(t.id)).items) } catch { /* pre-264 -> empty */ } }
                      }}>
                      <CalendarPlus size={14} /> Notes
                    </button>
                    <button
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                        border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 10px',
                        background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
                      onClick={async () => {
                        const open = docsFor === t.id ? null : t.id
                        setDocsFor(open); setDocItems([])
                        if (open) { try { setDocItems((await api.getCareRxDocChecklist(t.id)).items) } catch { /* guarded */ } }
                      }}>
                      <CalendarPlus size={14} /> Documents
                    </button>
                    <button
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                        border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 10px',
                        background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
                      onClick={async () => {
                        const open = histFor === t.id ? null : t.id
                        setHistFor(open); setHist(null)
                        if (open) { try { setHist(await api.getCareRxHistory360(t.id)) } catch { /* guarded */ } }
                      }}>
                      <CalendarPlus size={14} /> Fills & holds
                    </button>
                    <button
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                        border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 10px',
                        background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
                      onClick={() => { setEnding(ending === t.id ? null : t.id); setErr(null) }}>
                      <StopCircle size={14} /> End care
                    </button>
                  </span>
                )}
              </div>
              {notesFor === t.id && (
                <div style={{ marginTop: 8, border: '1px solid var(--color-border)', borderRadius: 10, padding: 10, fontSize: 12.5 }}>
                  {/* ASP-CRX-20 / spec §B: role-addressed lanes, not one blob. Append-only. */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <SearchableSelect value={noteLane} onChange={(v: string) => setNoteLane(v)}
                      options={[{ value: 'cdi_to_asp', label: 'CDI → ASP' }, { value: 'asp_to_cdi', label: 'ASP → CDI' }, { value: 'intake_a', label: 'Intake A' }, { value: 'intake_b', label: 'Intake B' }, { value: 'rph_to_intake', label: 'RPH → Intake' }]} />
                    <SearchableSelect value={''} onChange={(v: string) => { if (v) setNoteBody(v) }}
                      options={[{ value: '', label: 'Template…' },
                        { value: 'Dose 1 follow-up call completed. Tolerated well; no new symptoms reported. Next dose confirmed on schedule.', label: 'Dose-1 F/U' },
                        { value: 'Week-2 follow-up call completed. Adherence confirmed; no adverse effects reported. Education reinforced.', label: 'Week-2 F/U' },
                        { value: 'Month-3 follow-up completed. Therapy goals reviewed with patient; progressing as planned.', label: 'Month-3 F/U' },
                        { value: 'Outreach attempted — no answer; voicemail left with callback number. Will re-attempt per outreach schedule.', label: 'Outreach — no answer' },
                        { value: 'Refill coordination call completed. Delivery date confirmed with patient; no changes to therapy.', label: 'Refill coordination' }]} />
                    <input placeholder="Note" value={noteBody} onChange={e => setNoteBody(e.target.value)}
                      style={{ flex: 1, minWidth: 220, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5 }} />
                    <button disabled={!noteBody.trim()}
                      style={{ fontSize: 12, border: '1px solid var(--color-primary)', color: 'var(--color-primary)', borderRadius: 7, padding: '4px 12px', background: 'transparent', cursor: 'pointer' }}
                      onClick={async () => {
                        try {
                          await api.addCareRxTherapyNote(t.id, noteLane, noteBody.trim())
                          setNoteBody(''); setNotes((await api.listCareRxTherapyNotes(t.id)).items)
                        } catch (e: any) { window.alert(e?.body?.message || 'Note failed — lanes may await migration 264.') }
                      }}>Add</button>
                  </div>
                  {notes.map(n => (
                    <div key={n.id} style={{ padding: '3px 0', borderTop: '1px solid var(--color-border)' }}>
                      <b style={{ fontSize: 11 }}>{({ cdi_to_asp: 'CDI → ASP', asp_to_cdi: 'ASP → CDI', intake_a: 'Intake A', intake_b: 'Intake B', rph_to_intake: 'RPH → Intake' } as Record<string, string>)[n.lane] || n.lane}</b>
                      {' · '}{formatUsDate(n.created_at)} — {n.body}
                    </div>
                  ))}
                  {notes.length === 0 && <div style={{ color: 'var(--color-text-mute)' }}>No notes yet.</div>}
                </div>
              )}
              {histFor === t.id && hist && (
                <div style={{ marginTop: 8, border: '1px solid var(--color-border)', borderRadius: 10, padding: 10, fontSize: 12.5 }}>
                  {/* ASP-CRX-34 / script I1: fills history w/ dispensed/remaining + holds history — no tab-hopping */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <b>Fills</b>
                    <span>dispensed <b>{hist.fills.dispensed}</b>{hist.fills.authorized != null ? <> · authorized <b>{hist.fills.authorized}</b> · remaining <b style={{ color: (hist.fills.remaining ?? 1) === 0 ? '#b91c1c' : '#15803d' }}>{hist.fills.remaining}</b></> : <span style={{ color: 'var(--color-text-mute)' }}> · no signed plan total</span>}</span>
                  </div>
                  {hist.fills.items.map((f0: any, i: number) => (
                    <div key={i} style={{ padding: '2px 0', color: 'var(--color-text-soft)' }}>
                      {f0.drug || 'fill'} · {f0.fill_status} · {formatUsDate(f0.created_at)}{f0.delivered_at ? ` → delivered ${formatUsDate(f0.delivered_at)}` : ''}
                    </div>
                  ))}
                  {hist.fills.items.length === 0 && <div style={{ color: 'var(--color-text-mute)' }}>No fills yet.</div>}
                  <div style={{ marginTop: 8 }}><b>Holds</b></div>
                  {hist.holds.items.map((h0: any, i: number) => (
                    <div key={i} style={{ padding: '2px 0', color: 'var(--color-text-soft)' }}>
                      <b style={{ color: h0.status === 'active' ? '#b91c1c' : '#15803d' }}>{h0.status}</b>
                      {h0.trigger_analyte ? ` · ${h0.trigger_analyte}` : ''} · opened {formatUsDate(h0.opened_at)}{h0.cleared_at ? ` → cleared ${formatUsDate(h0.cleared_at)}` : ''}
                      {h0.reason ? <span style={{ color: 'var(--color-text-mute)' }}> — {h0.reason}</span> : null}
                    </div>
                  ))}
                  {hist.holds.items.length === 0 && <div style={{ color: 'var(--color-text-mute)' }}>No holds — clean history.</div>}
                </div>
              )}
              {docsFor === t.id && (
                <div style={{ marginTop: 8, border: '1px solid var(--color-border)', borderRadius: 10, padding: 10, fontSize: 12.5 }}>
                  {/* ASP-CRX-28 / script D4: the document checklist — derived statuses + the stored packet date */}
                  {docItems.map(d0 => (
                    <div key={d0.key} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                      <span style={{ minWidth: 130 }}>{d0.label}</span>
                      <b style={{ color: ['signed', 'complete', 'delivered'].includes(d0.status) ? '#15803d' : d0.status === 'unknown' ? 'var(--color-text-mute)' : '#b45309' }}>{d0.status}</b>
                      {d0.at && <span style={{ color: 'var(--color-text-mute)' }}>{formatUsDate(d0.at)}</span>}
                      {d0.key === 'welcome_packet' && d0.status === 'pending' && (
                        <button style={{ fontSize: 11.5, border: '1px solid var(--color-border)', borderRadius: 7, padding: '2px 9px', background: 'transparent', cursor: 'pointer' }}
                          onClick={async () => {
                            const dt = window.prompt('Welcome packet delivered on (YYYY-MM-DD):', new Date().toISOString().slice(0, 10))
                            if (!dt) return
                            try { await api.markCareRxWelcomePacket(t.id, dt); setDocItems((await api.getCareRxDocChecklist(t.id)).items) }
                            catch (e: any) { window.alert(e?.body?.message || 'Could not record it.') }
                          }}>Mark delivered</button>
                      )}
                    </div>
                  ))}
                  {docItems.length === 0 && <div style={{ color: 'var(--color-text-mute)' }}>Checklist unavailable.</div>}
                </div>
              )}
              {planFor === t.id && !t.is_terminal && (
                <div style={{ marginTop: 8 }}><CarePlanPanel therapyId={t.id} drugName={t.drug_name} phiValues={pcwPhiValues({ name: banner?.name ?? '', mrn: banner?.mrn ?? null, dob: banner?.dob ?? null })} /></div>
              )}
              {siteFor === t.id && !t.is_terminal && (
                <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--color-border)', borderRadius: 8,
                  display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5 }}>
                  {/* D-CRX-34 G3: site-of-care picker bound to the EXISTING pathway switch endpoint.
                      Andrew Q-SITE: exactly three professional-administration sites. Home = CareRx nurse
                      pathway (POT + invoice). AIC and Physician Clinic/Office = site pathways: site staff
                      administer, CareRx records a site-visit completion, NO CareRx nurse invoice (§13.7). */}
                  <b>Site of care — {t.drug_name}</b>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <SearchableSelect value={siteMode} onChange={v => setSiteMode(v)}
                      options={[
                        { value: '', label: 'Select site…' },
                        { value: 'home', label: 'Home (CareRx nurse — POT + invoice)' },
                        { value: 'infusion_center', label: 'Ambulatory Infusion Center (site-administered)' },
                        { value: 'clinic_site', label: 'Physician Clinic / Office (site-administered)' },
                      ]}
                      style={{ minWidth: 300 }} />
                    <input placeholder="Reason (required)" value={siteReason} onChange={e => setSiteReason(e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', minWidth: 220 }} />
                  </div>
                  {siteMode && siteMode !== 'home' && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                      Site pathway: the site administers and bills through its own channel. CareRx records a site-visit completion; no CareRx nurse invoice is generated (§13.7).
                    </div>
                  )}
                  {siteMsg && <div style={{ color: siteMsg.startsWith('✅') || siteMsg.startsWith('Re-consent') ? 'var(--color-text)' : '#b91c1c' }}>{siteMsg}</div>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button disabled={!siteMode || !siteReason.trim()}
                      style={{ fontSize: 12, border: '1px solid var(--color-primary)', borderRadius: 8, padding: '5px 12px',
                        background: 'var(--color-primary)', color: '#fff', cursor: siteMode && siteReason.trim() ? 'pointer' : 'not-allowed' }}
                      onClick={async () => {
                        setSiteMsg('')
                        try {
                          const res = await api.switchCareRxPathway(t.id, { mode: siteMode, reason: siteReason.trim() })
                          setSiteMsg(res.re_consent_required
                            ? 'Re-consent required: the consent document is mode-specific — flag this therapy for re-consent before the next visit (§13.5).'
                            : '✅ Site of care updated.')
                          setSiteReason('')
                          load()
                        } catch (e: any) { setSiteMsg(e?.message ? String(e.message) : "Could not update site of care.") }
                      }}>Update site</button>
                    <button style={{ fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 8, padding: '5px 12px',
                      background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
                      onClick={() => setSiteFor(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {potFor === t.id && !t.is_terminal && (
                <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--color-primary)', borderRadius: 8,
                  display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5 }}>
                  <b>Start Plan of Treatment — {t.drug_name}</b>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="date" value={potDate} onChange={e => setPotDate(e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)' }} />
                    <SearchableSelect value={potAgency} onChange={v => setPotAgency(v)}
                      options={[{ value: '', label: 'Organization (agency)…' }, ...agencies.map(a => ({ value: a.id, label: a.name }))]}
                      style={{ minWidth: 200 }} />
                  </div>
                  {potMsg && <div style={{ color: potMsg.startsWith('✅') ? 'var(--color-text)' : '#b91c1c' }}>{potMsg}</div>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button disabled={potBusy || !potDate}
                      style={{ fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--color-primary)',
                        background: 'var(--color-primary)', color: '#fff', borderRadius: 6, padding: '5px 12px' }}
                      onClick={async () => {
                        setPotBusy(true); setPotMsg('')
                        try {
                          const r = await api.startCareRxPot(t.id, { visit_date: potDate, agency_id: potAgency || undefined })
                          setPotMsg(`✅ POT scheduled${r.nurse ? ` — ${r.nurse.name}` : ' — in the Nurse POT queue to be claimed'}`)
                        } catch (e) {
                          const b = (e as { body?: { error?: string; message?: string } }).body
                          setPotMsg(b?.message ?? b?.error ?? (e as Error).message)
                        } finally { setPotBusy(false) }
                      }}>{potBusy ? 'Scheduling…' : 'Schedule visit'}</button>
                    <button onClick={() => setPotFor(null)}
                      style={{ fontSize: 12.5, cursor: 'pointer', border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 6, padding: '5px 12px' }}>Close</button>
                  </div>
                </div>
              )}
              {t.is_terminal && t.discharge_reason && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-mute)' }}>
                  Discharge reason: {t.discharge_reason}
                </div>
              )}
              {ending === t.id && !t.is_terminal && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <SearchableSelect
                    value={endState}
                    onChange={v => setEndState(v)}
                    ariaLabel="End-of-care state"
                    options={END_STATES.map(s => ({ value: s, label: s.replace(/([a-z])([A-Z])/g, '$1 $2') }))}
                    style={{ minWidth: 190, fontSize: 13 }}
                  />
                  <input value={endReason} onChange={e => setEndReason(e.target.value)} placeholder="End-of-care reason (required)"
                    style={{ flex: 1, minWidth: 220, fontSize: 13, padding: '5px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
                  <button disabled={busy} onClick={() => endCare(t.id)}
                    style={{ fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, padding: '6px 12px',
                      background: '#991b1b', color: '#fff', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                    Confirm end of care
                  </button>
                  {err && <span style={{ fontSize: 12, color: '#991b1b' }}>{err}</span>}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
